<template>
  <Menubar
    :model="_navItems"
    class="min-h-[8vh] relative"
    :pt="{ end: { class: 'self-center flex-1 min-w-0' } }"
  >
    <!-- Menu hamburger à gauche -->
    <template
      v-if="HasToggleableMenu"
      #start
    >
      <button
        ref="hamburgerMenuRef"
        aria-label="Menu"
        class="p-1 mr-5 -ml-1 text-gray-500 rounded-md md:block"
        @click="toggleSidebar"
      >
        <svg
          class="w-6 h-6"
          aria-hidden="true"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            clip-rule="evenodd"
            fill-rule="evenodd"
            d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
          />
        </svg>
      </button>
      <Tag
        v-if="config.public.appEnvironment !== 'production'"
        severity="warn"
        class="fixed left-12 z-50"
      >
        Preprod
      </Tag>
    </template>

    <!-- Menu à droite -->
    <template #end>
      <!-- Panneau recherche compacte (< xl) -->
      <div
        v-if="showMobileSearch"
        class="absolute inset-x-0 top-full z-50 p-3 bg-white border-b border-slate-200 shadow-md xl:hidden"
      >
        <GlobalSearchBar />
      </div>

      <div class="sm:flex sm:items-center sm:ml-2">
        <!-- Recherche globale (≥ xl) : dans le flux, rétrécit au lieu de chevaucher les actions de droite -->
        <div class="hidden xl:block flex-1 min-w-0 max-w-[34rem] pr-5 mr-auto">
          <GlobalSearchBar />
        </div>

        <!-- Recherche compacte (< xl) -->
        <button
          type="button"
          class="p-1 mr-4 rounded-md border border-transparent text-[var(--ocv-secondary-700)] transition xl:hidden hover:border-[var(--ocv-secondary-500)] hover:bg-[var(--ocv-secondary-50)]"
          aria-label="Rechercher"
          @click="showMobileSearch = !showMobileSearch"
        >
          <i
            class="pi pi-search text-xl"
            aria-hidden="true"
          />
        </button>

        <!-- Bouton Export -->
        <div
          v-if="visibleExportItems.length > 0"
          class="mr-5"
        >
          <button
            aria-label="Ouvrir la modale"
            class="p-1 ml-2 rounded-md border border-transparent text-[var(--ocv-secondary-700)] transition hover:border-[var(--ocv-secondary-500)] hover:bg-[var(--ocv-secondary-50)]"
            @click="toggleExport"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="size-7"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
              />
            </svg>
          </button>

          <Menu
            ref="menuExport"
            :model="visibleExportItems"
            :popup="true"
            class="w-full md:w-60"
          >
            <template #start>
              <div class="flex gap-2 items-center py-3 px-4 border-b border-gray-200">
                <span class="inline-flex gap-2 items-center py-2 px-2">
                  <img
                    src="/images/icon_OCV.png"
                    alt="Logo OCV"
                    class="w-auto h-12"
                  >
                  <span class="text-xl font-semibold">OCV</span>
                </span>
              </div>
            </template>

            <template #submenulabel="{ item }">
              <span class="font-bold text-primary">{{ item.label }}</span>
            </template>

            <template #item="{ item, props }">
              <a
                v-ripple
                class="flex gap-2 items-center py-1 px-2 w-full"
                v-bind="props.action"
              >
                <span
                  v-if="item.icon"
                  :class="item.icon"
                />
                <span>{{ item.label }}</span>
                <Badge
                  v-if="item.badge"
                  class="ml-auto"
                  :value="item.badge"
                />
                <span
                  v-if="item.shortcut"
                  class="py-0.5 px-2 ml-auto text-xs rounded border border-surface bg-emphasis text-muted-color"
                >
                  {{ item.shortcut }}
                </span>
              </a>
            </template>
          </Menu>
        </div>

        <div
          v-if="user"
          class="mr-5"
        >
          <button
            type="button"
            class="p-1 rounded-md border border-transparent text-[var(--ocv-secondary-700)] transition hover:border-[var(--ocv-secondary-500)] hover:bg-[var(--ocv-secondary-50)] focus:outline-none focus:ring-2 focus:ring-[var(--ocv-secondary-500)]"
            aria-label="Personnaliser les couleurs"
            title="Personnaliser les couleurs"
            @click="openThemeColorsDialog"
          >
            <i
              class="pi pi-palette text-xl"
              aria-hidden="true"
            />
          </button>
        </div>

        <div
          v-if="user"
          class="mr-5"
        >
          <NotificationBell />
        </div>

        <!-- Notification échecs d'email -->
        <div
          v-if="emailFailuresCount > 0"
          class="mr-5"
        >
          <NuxtLink :to="`/email-failures`">
            <button
              class="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 text-white transition hover:bg-red-700 shadow-sm"
              aria-label="Échecs d'envoi d'emails"
            >
              <i class="pi pi-send text-base" />
              <span class="text-xs font-semibold">{{ emailFailuresCount }}</span>
              <i class="pi pi-times-circle text-sm" />
            </button>
          </NuxtLink>
        </div>

        <!-- Utilisateur connecté -->
        <div
          v-if="user"
          class="relative"
        >
          <Button
            as="a"
            label="Aller sur l'ancien site"
            :href="oldURL"
            target="_blank"
            rel="noopener"
            class="mr-8"
          />

          <button
            v-if="user.icon"
            type="button"
            class="flex text-sm rounded-full border-2 border-transparent transition focus:border-gray-300 focus:outline-none"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            @click="toggle"
          >
            <Avatar
              class="mr-2"
              icon="pi pi-user"
              shape="circle"
              size="xlarge"
            />
          </button>

          <span
            v-else
            class="inline-flex rounded-md"
          >
            <button
              type="button"
              class="inline-flex items-center py-2 px-3 text-sm font-medium leading-4 text-gray-500 rounded-md border transition dark:text-gray-300 focus:outline-none hover:light:text-gray-700"
              aria-haspopup="true"
              aria-controls="overlay_menu"
              @click="toggle"
            >
              {{ user.name }}
              <svg
                class="ml-2 -mr-0.5 w-4 h-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>
          </span>
          <Menu
            id="overlay_menu"
            ref="menu"
            :model="profileItems"
            :popup="true"
          />
        </div>
        <!-- Utilisateur non connecté -->
        <div
          v-else
          class="relative"
        >
          <NuxtLink to="/login">
            <Button
              type="button"
              icon="pi pi-sign-in"
              label="Se connecter"
              :link="true"
            />
          </NuxtLink>
        </div>
      </div>
    </template>
  </Menubar>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ref, computed, watch, onMounted } from "vue";
import { useSidebarStore } from "~/stores/sidebar";
import type { MenuItem } from "primevue/menuitem";
import Menu from "primevue/menu";
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import Badge from "primevue/badge";
import NotificationBell from "~/components/atoms/NotificationBell.vue";
import GlobalSearchBar from "~/components/GlobalSearchBar.vue";
import { useRuntimeConfig } from "#app";
import { useDialog } from "primevue/usedialog";

import AccountancyModal from "~/components/atoms/modal/AccountancyModal.vue";
import ExportModal from "~/components/atoms/modal/ExportModal.vue";
import ReportingModal from "~/components/atoms/modal/ReportingModal.vue";
import UserCreate from "~/components/user/Create.vue";
import { COOKIE_KEYS, type UserStoreCookie } from "~/constants/cookies";
import { getAvailableSpaces, updateSelectedSpace } from "~/utils/spaceManagement";
import UserThemeColorsForm from "~/components/user/ThemeColorsForm.vue";
import Role from "~/types/Role";
import Permissions from "~/types/Permissions";
import { filterMenuItemsByAccess, type PermissionMenuItem } from "~/utils/menuAccess";
import { canCreateUserWithRoles } from "~/utils/canEditUserTarget";

useHead({
  link: [{ rel: "icon", href: "data:," }],
});

const props = defineProps<{
  navItems?: MenuItem[];
  HasToggleableMenu?: boolean;
}>();

const hamburgerMenuRef = ref<HTMLElement | null>(null);
defineExpose({ hamburgerMenuRef });

const { toggleSidebar } = useSidebarStore();
const config = useRuntimeConfig();
const tenantName = ref(useTenant());

const authService = useAuthService();
const userStore = useUserStore();
const userStoreCookie = useCookie<UserStoreCookie>(COOKIE_KEYS.USER_STORE);
const user = await userStore.getUser();

const menu = ref();
const menuExport = ref();

// Repli recherche compacte (< xl) : ouvre/ferme le panneau déroulant.
const showMobileSearch = ref(false);
// Referme le panneau compact après une navigation (ex. clic sur un résultat).
watch(() => useRoute().fullPath, () => {
  showMobileSearch.value = false;
});

const dialog = useDialog();

const _navItems = ref<MenuItem[]>(props.navItems || []);
const profileItems = ref<MenuItem[]>([]);

// Gestion des échecs d'email
const { getEmailFailures } = useEmailFailures();
const { $eventBus, $hasPermission, $hasRole } = useNuxtApp();
const emailFailuresCount = ref(0);

/**
 * Charge le nombre d'échecs d'email actifs
 */
const loadEmailFailuresCount = async () => {
  try {
    const response = await getEmailFailures({
      show_archived: false,
      no_pagination: true,
    });

    if (response.status === 200 && response.data) {
      emailFailuresCount.value = response.data.meta.active_count;
    }
  }
  catch (error) {
    console.error("Failed to load email failures count:", error);
  }
};

// Chaque action porte la permission du catalogue qu'elle exige ; le modèle affiché
// est filtré (groupes vides retirés, bouton masqué si plus rien n'est accessible).
// computed : `disabled` suit les droits, chargés de façon asynchrone.
const exportItems = computed<PermissionMenuItem[]>(() => [
  {
    label: "Ajout rapide",
    items: [
      {
        label: "Utilisateur",
        permissions: [Permissions.UsersCreate],
        // Création normale : les rôles envoyés exigent aussi app.roles.assign (ADR 0002).
        disabled: !canCreateUserWithRoles(false, $hasPermission),
        command: () => {
          if (!canCreateUserWithRoles(false, $hasPermission)) return;
          dialog.open(UserCreate, {
            props: {
              modal: true,
            },
            onClose: (_options) => { },
          });
        },
      },
      {
        label: "Commerçant",
        permissions: [Permissions.MerchantsCreate],
        command: () => console.log("Ajout commerçant"),
      },
      {
        label: "Contact",
        permissions: [Permissions.ContactsCreate],
        command: () => console.log("Ajout contact"),
      },
      {
        label: "Abonnement",
        permissions: [Permissions.SubscriptionsCreate],
        command: () => console.log("Ajout abonnement"),
      },
    ],
  },
  {
    label: "Exports",
    items: [
      {
        label: "Exportateur",
        permissions: [Permissions.ReportingGenerate],
        command: () => {
          dialog.open(ExportModal, {
            props: {
              header: "Générer un export",
              modal: true,
              dismissableMask: false,
              draggable: false,
              style: {
                width: "min(94vw, 56rem)",
                margin: "0.75rem",
              },
              position: "topright",
              pt: {
                root: {
                  class: "!border-0 !overflow-hidden !rounded-2xl !shadow-xl",
                },
                header: {
                  class: "!bg-surface-0 !border-b !border-surface-200",
                },
                content: {
                  class: "!p-0 !bg-surface-0",
                },
                mask: {
                  class: "backdrop-blur-sm",
                },
              },
            },
          });
        },
      },
      {
        label: "Reporting",
        permissions: [Permissions.ReportingGenerate],
        command: () => {
          dialog.open(ReportingModal, {
            props: {
              header: "Générer un reporting",
              modal: true,
              dismissableMask: true,
              draggable: false,
              style: {
                width: "min(94vw, 56rem)",
                margin: "0.75rem",
              },
              position: "topright",
              pt: {
                root: {
                  class: "!border-0 !overflow-hidden !rounded-2xl !shadow-xl",
                },
                header: {
                  class: "!bg-surface-0 !border-b !border-surface-200",
                },
                content: {
                  class: "!p-0 !bg-surface-0",
                },
                mask: {
                  class: "backdrop-blur-sm",
                },
              },
            },
          });
        },
      },
      {
        label: "Export Comptable",
        permissions: [Permissions.AccountingExportsGenerate],
        command: () => {
          dialog.open(AccountancyModal, {
            props: {
              header: "Générer un export comptable",
              modal: true,
              dismissableMask: true,
              draggable: false,
              style: {
                width: "min(94vw, 56rem)",
                margin: "0.75rem",
              },
              position: "topright",
              pt: {
                root: {
                  class: "!border-0 !overflow-hidden !rounded-2xl !shadow-xl",
                },
                header: {
                  class: "!bg-surface-0 !border-b !border-surface-200",
                },
                content: {
                  class: "!p-0 !bg-surface-0",
                },
                mask: {
                  class: "backdrop-blur-sm",
                },
              },
            },
          });
        },
      },
    ],
  },
]);

const visibleExportItems = computed(() =>
  filterMenuItemsByAccess(exportItems.value, {
    hasPermission: $hasPermission,
    hasRole: $hasRole,
  }),
);

const oldURL = computed(() => {
  const serverOldHost = new URL(config.public.serverOldHost);
  return `${serverOldHost.protocol}//${tenantName.value}.${serverOldHost.host}`;
});

onMounted(() => {
  if (user.value) {
    // Charger le compte des échecs d'email
    loadEmailFailuresCount();

    // Écouter les événements de statut de livraison pour rafraîchir le badge
    $eventBus.on("email-deliver-status", () => {
      loadEmailFailuresCount();
    });

    profileItems.value = [
      {
        label: "Mon compte",
        items: [
          {
            label: "Mon profil",
            icon: "pi pi-user",
            command: async () => {
              await navigateTo("/profile");
            },
          },
          {
            label: "Se déconnecter",
            icon: "pi pi-sign-out",
            command: async () => {
              await authService.logout();
              await authService.resetAuthCookie();
              await authService.resetConnectedUserData();
              await navigateTo("/login");
            },
          },
        ],
      },
    ];
    const availableSpaces = getAvailableSpaces(user.value?.roles ?? []);
    if (availableSpaces.length > 1) {
      profileItems.value[0].items?.splice(1, 0, {
        label: "Changer d’espace",
        icon: "pi pi-th-large",
        command: async () => {
          updateSelectedSpace(userStore, userStoreCookie, null);
          await navigateTo("/choice-space");
        },
      });
    }
  }
});

const toggle = (event: Event) => menu.value?.toggle(event);
const toggleExport = (event: Event) => menuExport.value?.toggle(event);
const openThemeColorsDialog = () => {
  dialog.open(UserThemeColorsForm, {
    props: {
      header: "Personnaliser les couleurs",
      modal: true,
      dismissableMask: true,
      draggable: false,
      style: {
        width: "min(94vw, 64rem)",
        margin: "0.75rem",
      },
      breakpoints: {
        "960px": "94vw",
        "640px": "98vw",
      },
    },
  });
};
</script>
